"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { CircleCheck as CheckCircle, ShieldCheck, UserCheck, Star } from 'lucide-react';
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Trust() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.from(".climate-left > .climate-reveal", {
        opacity: 0,
        y: 16,
        duration: 0.62,
        ease: "cubic-bezier(.2,.7,.2,1)",
        stagger: 0.09,
        scrollTrigger: {
          trigger: container.current,
          start: "top 82%"
        }
      });
      gsap.from(".climate-block", {
        opacity: 0,
        y: 16,
        duration: 0.62,
        ease: "cubic-bezier(.2,.7,.2,1)",
        stagger: 0.06,
        scrollTrigger: {
          trigger: ".climate-right",
          start: "top 82%"
        }
      });
    });
  }, {
    scope: container
  });
  const features = [{
    icon: ShieldCheck,
    title: "Credential Reviews",
    caption: "Contractors submit trade background details and relevant business registrations during onboarding."
  }, {
    icon: UserCheck,
    title: "Identity Screenings",
    caption: "We verify identity information for independent pros before matching them with homeowners."
  }, {
    icon: Star,
    title: "Homeowner Feedback",
    caption: "Real project reviews from local homeowners help you evaluate independent pros based on recent service experiences."
  }, {
    icon: CheckCircle,
    title: "Direct Quotes",
    caption: "Pros inspect the issue and provide a direct quote before work starts, so you understand your service terms."
  }];
  return <section data-section-id="24587" id="trustbuildingfeatures" data-nav="light" className="bg-background py-28 md:py-40">
      <div ref={container} className="mx-auto max-w-[1360px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-12">
          {}
          <div className="climate-left lg:col-span-5">
            <div className="climate-reveal mb-5 flex items-center gap-2">
              <span aria-hidden="true" className="inline-block h-[14px] w-[3px]" style={{
              backgroundColor: "var(--primary)"
            }} />
              <span className="font-sans text-xs font-semibold uppercase tracking-[0.18em] sm:text-[0.8125rem]" style={{
              color: "var(--primary)"
            }}>
                {"Safety & Trust"}
              </span>
              <span aria-hidden="true" className="inline-block h-[14px] w-[3px]" style={{
              backgroundColor: "var(--primary)"
            }} />
            </div>

            <h2 className="climate-reveal font-mono font-bold uppercase text-foreground" style={{
            fontSize: "clamp(2.5rem, 5.6vw, 5.25rem)",
            letterSpacing: "-0.005em",
            lineHeight: 0.96
          }}>
              {"Independent pros connected with your home."}
            </h2>

            <p className="climate-reveal mt-8 font-default text-muted-foreground" style={{
            maxWidth: "42ch",
            fontSize: "clamp(1.125rem, 1.35vw, 1.3125rem)",
            lineHeight: 1.55,
            letterSpacing: "-0.005em"
          }}>
              {"Handy Heroes is a marketplace that connects Atlanta homeowners with independent local contractors. We review contractor submissions and past trade history during onboarding so you can connect with independent specialists across your neighborhood."}
            </p>
          </div>

          {}
          <div className="climate-right lg:col-span-7">
            <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-12">
              {features.map((feature, i) => {
              const Icon = feature.icon;
              const isTopRow = i < 2;
              return <div key={feature.title} data-index={i} className={`climate-block group relative ${!isTopRow ? "sm:border-t sm:border-border sm:pt-12" : ""} ${i === 1 || i === 3 ? "" : ""} ${i > 0 ? "border-t border-border pt-10 sm:border-t-0 sm:pt-0" : ""} ${!isTopRow ? "sm:!border-t sm:!pt-12" : ""}`}>
                    <Icon className="mb-5 h-10 w-10 text-foreground transition-colors duration-[220ms] group-hover:text-primary" strokeWidth={1.75} aria-hidden="true" />
                    <h3 className="font-default text-[1.0625rem] font-semibold leading-tight text-foreground sm:text-[1.15rem] lg:text-[1.25rem]">
                      {feature.title}
                    </h3>
                    <p className="mt-3 font-default text-[0.9375rem] leading-[1.6] text-muted-foreground sm:text-base">
                      {feature.caption}
                    </p>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}